import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { MOBILE } from '../../../utils/constants';
import { Link } from 'react-resource-router';
import ProductTag from './ProductTag';
import Tags from './Tags';
import ArticleType from './ArticleType';
import PostMetadata from './PostMetadata';
import type { NormalizedPost } from 'typings/posts';
// import { Renderer } from '~src/components/Editor/Renderer';

interface Props {
  post: NormalizedPost;
}

const ForumPost = ({ post }: Props) => {
  return (
    <article data-testid="forum-post" css={postContainerStyles}>
      <div css={headerStyles}>
        <h3 css={titleStyles}>
          <Link to={`/forumsv2/${post.id}`} css={titleLinkStyles}>
            {post.title}
          </Link>
        </h3>
        <div css={statusStyles}>
          {post.isPinned && <span css={pinnedStyle}>📌 Pinned</span>}
          {post.isLocked && <span css={lockedStyle}>🔒 Locked</span>}
        </div>
      </div>
      {/* <Renderer content={post.content} /> */}
      <PostMetadata
        views={post.views}
        comments={post.comments}
        likes={post.likes}
        author={post.author}
        timeAgo={post.timeAgo}
      />
      <div css={tagsContainerStyles}>
        {post.productTag && <ProductTag productTag={post.productTag} />}
        <ArticleType articleType={post.articleType} />
        <Tags tags={post.tags} />
      </div>
    </article>
  );
};

const postContainerStyles = css({
  padding: '16px',
  backgroundColor: token('elevation.surface'),
  border: `1px solid ${token('color.border')}`,
  borderRadius: '8px',
  marginBottom: '16px',
  boxShadow: token('elevation.shadow.raised'),
  [MOBILE.MEDIA_QUERY]: {
    padding: '12px',
    marginBottom: '12px',
  },
});

const titleStyles = css({
  fontSize: '18px',
  fontWeight: 600,
  lineHeight: '1.4',
  margin: '0 0 8px 0',
  color: token('color.text'),
  fontFamily: 'Charlie Text, sans-serif',
  [MOBILE.MEDIA_QUERY]: {
    fontSize: '16px',
  },
});

const titleLinkStyles = css({
  color: token('color.text'),
  textDecoration: 'none',
  '&:hover': {
    color: token('color.text'),
    textDecoration: 'underline',
    textDecorationColor: token('color.text'),
  },
  '&:visited': {
    color: token('color.text'),
    textDecoration: 'none',
  },
});

const tagsContainerStyles = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  [MOBILE.MEDIA_QUERY]: {
    gap: '6px',
  },
});

const headerStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '8px',
  [MOBILE.MEDIA_QUERY]: {
    flexDirection: 'column',
    gap: '4px',
  },
});

const statusStyles = css({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  [MOBILE.MEDIA_QUERY]: {
    gap: '6px',
  },
});

const pinnedStyle = css({
  fontSize: '12px',
  color: token('color.text.success'),
  fontWeight: 500,
  backgroundColor: token('color.background.success'),
  padding: '2px 6px',
  borderRadius: '4px',
  border: `1px solid ${token('color.border.success')}`,
});

const lockedStyle = css({
  fontSize: '12px',
  color: token('color.text.warning'),
  fontWeight: 500,
  backgroundColor: token('color.background.warning'),
  padding: '2px 6px',
  borderRadius: '4px',
  border: `1px solid ${token('color.border.warning')}`,
});

export default ForumPost;
